import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Divider, List, ListItem, ListItemButton, ListItemText, ListItemSecondaryAction, Collapse, Chip, useTheme, alpha, Grid } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InsightsIcon from '@mui/icons-material/Insights';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import { api } from '@services';

// Define an interface for the investmentScore structure
interface InvestmentScoreObject {
  score: string | number;
  rationale: string;
}

interface AnalysisHistoryItem {
  id: string;
  hotel_id: string;
  version: number;
  created_at: string;
  parameters: string[] | null;
  analysis_data: {
    marketAnalysis: {
      summary: string;
      competitivePosition: string;
      demandDrivers: string[];
      risks: string[];
    };
    propertyAssessment: {
      strengths: string[];
      weaknesses: string[];
      recommendations: string[];
    };
    valuationInsights: {
      estimatedValue: {
        low: number;
        high: number;
      };
      valueDrivers: string[];
      potentialROI: string;
    };
    investmentScore: number | InvestmentScoreObject; // Updated to support both formats
  };
}

interface HotelAnalysisHistoryProps {
  hotelId: string;
}

// Helper function to extract the numeric score from investmentScore
const getScoreValue = (investmentScore: number | InvestmentScoreObject): number => {
  if (typeof investmentScore === 'number') {
    return investmentScore;
  }

  // If it's an object, try to get the score property
  if (investmentScore && typeof investmentScore === 'object' && 'score' in investmentScore) {
    const score = investmentScore.score;
    return typeof score === 'number' ? score : parseFloat(String(score)) || 0;
  }

  return 0; // Default fallback
};

// Helper function to format the score for display
const formatScore = (investmentScore: number | InvestmentScoreObject): string => {
  if (typeof investmentScore === 'number') {
    return String(investmentScore);
  }

  if (investmentScore && typeof investmentScore === 'object' && 'score' in investmentScore) {
    return String(investmentScore.score);
  }

  return 'N/A';
};

const HotelAnalysisHistory: React.FC<HotelAnalysisHistoryProps> = ({ hotelId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisHistory, setAnalysisHistory] = useState<AnalysisHistoryItem[]>([]);
  const [expandedAnalysis, setExpandedAnalysis] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!hotelId) return;

    const fetchAnalysisHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/hotels/${hotelId}/ai-analysis/history`);
        setAnalysisHistory(response.data);
      } catch (error: any) {
        console.error('Error fetching analysis history:', error);
        setError(error.response?.data?.error || 'Failed to load analysis history');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisHistory();
  }, [hotelId]);

  const toggleExpand = (id: string) => {
    if (expandedAnalysis === id) {
      setExpandedAnalysis(null);
    } else {
      setExpandedAnalysis(id);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Updated to accept number or InvestmentScoreObject
  const getInvestmentScoreColor = (scoreData: number | InvestmentScoreObject) => {
    const score = getScoreValue(scoreData);

    if (score >= 80) return theme.palette.success.main;
    if (score >= 60) return theme.palette.info.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (analysisHistory.length === 0) {
    return (
      <Box>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontWeight: 600,
            mb: 3,
          }}
        >
          <HistoryIcon />
          Analysis History
        </Typography>

        <Alert severity="info">No analysis history found for this hotel. Run an AI analysis first to see history.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontWeight: 600,
          mb: 3,
        }}
      >
        <HistoryIcon />
        Analysis History
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        View past AI analyses of this property to track changes in valuation and insights over time.
      </Typography>

      <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden' }}>
        {analysisHistory.map((analysis, index) => {
          // Safety check for the analysis data
          if (!analysis || !analysis.analysis_data) {
            return null; // Skip this item if analysis_data is missing
          }

          const analysisData = analysis.analysis_data;
          const hasInvestmentScore = analysisData && 'investmentScore' in analysisData;
          const hasMarketAnalysis = analysisData && analysisData.marketAnalysis;
          const hasPropertyAssessment = analysisData && analysisData.propertyAssessment;
          const hasValuationInsights = analysisData && analysisData.valuationInsights;

          return (
            <React.Fragment key={analysis.id}>
              {index > 0 && <Divider component="li" />}
              <Paper
                elevation={0}
                sx={{
                  mb: 0.5,
                  borderRadius: index === 0 ? '8px 8px 0 0' : index === analysisHistory.length - 1 ? '0 0 8px 8px' : 0,
                  bgcolor: expandedAnalysis === analysis.id ? alpha(theme.palette.primary.light, 0.1) : 'transparent',
                  transition: 'background-color 0.3s ease',
                }}
              >
                <ListItemButton
                  component="div"
                  onClick={() => toggleExpand(analysis.id)}
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.light, 0.05),
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Analysis {analysis.version}
                        </Typography>
                        {hasInvestmentScore && (
                          <Chip
                            size="small"
                            label={`Score: ${formatScore(analysisData.investmentScore)}`}
                            sx={{
                              bgcolor: hasInvestmentScore ? getInvestmentScoreColor(analysisData.investmentScore) : theme.palette.grey[500],
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <DateRangeIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(analysis.created_at)}
                          </Typography>
                        </Box>

                        {analysis.parameters && analysis.parameters.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {analysis.parameters.map((param, i) => (
                              <Chip key={i} label={param} size="small" variant="outlined" sx={{ height: 20 }} />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>{expandedAnalysis === analysis.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ListItemSecondaryAction>
                </ListItemButton>
                <Collapse in={expandedAnalysis === analysis.id} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 3, pt: 0 }}>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={3}>
                      {/* Market Analysis */}
                      {hasMarketAnalysis && (
                        <Grid item xs={12} md={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.7),
                              borderRadius: 2,
                              height: '100%',
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InsightsIcon color="primary" />
                              Market Analysis
                            </Typography>

                            {analysisData.marketAnalysis.summary && (
                              <Typography variant="body2" paragraph>
                                {analysisData.marketAnalysis.summary}
                              </Typography>
                            )}

                            {analysisData.marketAnalysis.competitivePosition && (
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Competitive Position: {analysisData.marketAnalysis.competitivePosition}
                              </Typography>
                            )}

                            {analysisData.marketAnalysis.demandDrivers && analysisData.marketAnalysis.demandDrivers.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, mt: 1.5 }}>
                                  Demand Drivers:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.marketAnalysis.demandDrivers.map((driver, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={driver} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}

                            {analysisData.marketAnalysis.risks && analysisData.marketAnalysis.risks.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, mt: 1.5 }}>
                                  Market Risks:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.marketAnalysis.risks.map((risk, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={risk} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}
                          </Paper>
                        </Grid>
                      )}

                      {/* Property Assessment */}
                      {hasPropertyAssessment && (
                        <Grid item xs={12} md={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.7),
                              borderRadius: 2,
                              height: '100%',
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon color="primary" />
                              Property Assessment
                            </Typography>

                            {analysisData.propertyAssessment.strengths && analysisData.propertyAssessment.strengths.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Strengths:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.propertyAssessment.strengths.map((strength, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={strength} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}

                            {analysisData.propertyAssessment.weaknesses && analysisData.propertyAssessment.weaknesses.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, mt: 1.5 }}>
                                  Weaknesses:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.propertyAssessment.weaknesses.map((weakness, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={weakness} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}

                            {analysisData.propertyAssessment.recommendations && analysisData.propertyAssessment.recommendations.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, mt: 1.5 }}>
                                  Recommendations:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.propertyAssessment.recommendations.map((rec, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}
                          </Paper>
                        </Grid>
                      )}

                      {/* Valuation Insights */}
                      {hasValuationInsights && (
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.background.default, 0.7),
                              borderRadius: 2,
                            }}
                          >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachMoneyIcon color="primary" />
                              Valuation Insights
                            </Typography>

                            {analysisData.valuationInsights.estimatedValue && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Estimated Value Range
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    ${analysisData.valuationInsights.estimatedValue.low?.toLocaleString() || 0} - ${analysisData.valuationInsights.estimatedValue.high?.toLocaleString() || 0}
                                  </Typography>
                                </Box>

                                {analysisData.valuationInsights.potentialROI && (
                                  <Box>
                                    <Typography variant="body2" color="text.secondary">
                                      Potential ROI
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                      {analysisData.valuationInsights.potentialROI}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {analysisData.valuationInsights.valueDrivers && analysisData.valuationInsights.valueDrivers.length > 0 && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Value Drivers:
                                </Typography>
                                <List dense disablePadding>
                                  {analysisData.valuationInsights.valueDrivers.map((driver, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                                      <ListItemText primary={driver} primaryTypographyProps={{ variant: 'body2' }} />
                                    </ListItem>
                                  ))}
                                </List>
                              </>
                            )}
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default HotelAnalysisHistory;
